import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useProperties, useDeleteProperty } from 'hooks/api/useProperty';
import type { Property, PropertySearchRequest } from 'api/types';
import PropertyModal from 'components/modals/modules/property-module/PropertyModal';
import PropertyViewModal from 'components/modals/modules/property-module/PropertyViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const propertyTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Resort', label: 'Resort' },
  { value: 'Serviced Apartment', label: 'Serviced Apartment' },
];

const starRatingOptions = [
  { value: '', label: 'All Ratings' },
  { value: '1 Star', label: '1 Star' },
  { value: '2 Star', label: '2 Star' },
  { value: '3 Star', label: '3 Star' },
  { value: '4 Star', label: '4 Star' },
  { value: '5 Star', label: '5 Star' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'propertyCode', label: 'Property Code', field: 'propertyCode', required: false, defaultVisible: true, sortable: true },
  { id: 'propertyName', label: 'Property Name', field: 'propertyName', required: false, defaultVisible: true, sortable: true },
  { id: 'propertyType', label: 'Type', field: 'propertyType', required: false, defaultVisible: true, sortable: true },
  { id: 'starRating', label: 'Star Rating', field: 'starRating', required: false, defaultVisible: true, sortable: true },
  { id: 'city', label: 'City', field: 'city', required: false, defaultVisible: true, sortable: true },
  { id: 'phonePrimary', label: 'Phone', field: 'phonePrimary', required: false, defaultVisible: true, sortable: false },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'email', label: 'Email', field: 'email', required: false, defaultVisible: false, sortable: false },
  { id: 'state', label: 'State', field: 'state', required: false, defaultVisible: false, sortable: true },
  { id: 'totalRooms', label: 'Total Rooms', field: 'totalRooms', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 0.5rem)',
          right: 0,
          width: '320px',
          maxHeight: '500px',
          background: 'var(--bg-primary)',
          border: '2px solid var(--primary)',
          borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// PROPERTY CARD COMPONENT (Grid View)
// ============================================================================

interface PropertyCardProps {
  property: Property;
  onView: (property: Property) => void;
  onEdit: (property: Property) => void;
  onCopy: (property: Property) => void;
  onDelete: (property: Property) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, onView, onEdit, onCopy, onDelete }) => {
  const menuItems: ActionMenuItem[] = [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(property) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(property) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => onCopy(property) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(property), variant: 'danger' },
  ];

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {property.propertyCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {property.propertyName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Type</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{property.propertyType || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rating</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{property.starRating || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>City</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{property.city || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${property.isActive ? 'active' : 'inactive'}`}>
            {property.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PropertyMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const PropertyMasterScreen: React.FC<PropertyMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<PropertySearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, refetch } = useProperties(queryParams);
  const { deleteProperty, loading: isDeleting } = useDeleteProperty();

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterType, setFilterType] = useState('');
  const [filterStarRating, setFilterStarRating] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Property | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Search debounce (server-side, 400ms) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Server-side pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('property-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('property-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('property-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- F2 keyboard shortcut for search popup ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // --- View handler ---
  const handleView = useCallback((property: Property) => {
    const viewId = tabId ? `${tabId}-view-modal-${property.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(property);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((propertyId: string) => {
    const property = data.find(p => p.id === propertyId);
    if (property) {
      setSelectedRecord(property);
      setViewModalOpen(true);
    }
  }, [data]);

  // --- Edit handler ---
  const handleEdit = useCallback((property: Property) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${property.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(property);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Copy handler (multi-instance) ---
  const handleCopy = useCallback((property: Property) => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(property);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((property: Property) => {
    setSelectedRecord(property);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteProperty(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.propertyName}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteProperty, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (recordCode) {
      const prop = data.find(p => p.propertyCode === recordCode);
      if (prop) setSelectedRecord(prop);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<Property>[] = useMemo(() => [
    { id: 'propertyCode', header: 'Property Code', accessorKey: 'propertyCode', size: 130, enableSorting: true },
    { id: 'propertyName', header: 'Property Name', accessorKey: 'propertyName', size: 200, enableSorting: true },
    { id: 'propertyType', header: 'Type', accessorKey: 'propertyType', size: 150, enableSorting: true },
    { id: 'starRating', header: 'Star Rating', accessorKey: 'starRating', size: 120, enableSorting: true },
    { id: 'city', header: 'City', accessorKey: 'city', size: 140, enableSorting: true },
    { id: 'phonePrimary', header: 'Phone', accessorKey: 'phonePrimary', size: 140 },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 180 },
    { id: 'state', header: 'State', accessorKey: 'state', size: 120, enableSorting: true },
    { id: 'totalRooms', header: 'Total Rooms', accessorKey: 'totalRooms', size: 110, enableSorting: true },
  ], []);

  // --- Row actions (matches standard: View, Edit, Copy, Delete) ---
  const getRowActions = useCallback((property: Property): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(property) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(property) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(property) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(property), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        id: 'search',
        icon: <IconSystem.search size={18} />,
        label: 'Search Properties',
        onClick: () => setSearchPopupOpen(true),
        tooltip: 'Search Properties (F2)',
        variant: 'icon',
      },
    ];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add Property',
        onClick: handleAddNew,
        tooltip: 'Add New Property',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    });
    if (allowedActions.includes('export')) {
      buttons.push({
        id: 'export',
        icon: <IconSystem.download size={18} />,
        label: 'Export',
        onClick: () => console.log('Export clicked'),
        tooltip: 'Export',
        variant: 'icon',
      });
    }
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      propertyType: filterType || undefined,
      starRating: filterStarRating || undefined,
      city: filterCity || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterType, filterStarRating, filterCity, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterType('');
    setFilterStarRating('');
    setFilterCity('');
    setFilterStatus('');
    setQueryParams(prev => ({
      ...prev,
      propertyType: undefined,
      starRating: undefined,
      city: undefined,
      state: undefined,
      includeInactive: undefined,
      page: 1,
    }));
  }, []);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Property Master"
        description="Manage hotel properties and their configurations"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search properties...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterType, filterStarRating, filterCity, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* REQUIRED: marginTop: '6px' — the ONLY allowed inline style on .module-content */}
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No properties found"
              frozenLeftColumns={['select', 'propertyCode']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(pagination.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  flex: 1,
                  overflow: 'auto',
                  alignContent: 'start',
                }}>
                  {data.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.home size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No properties found</p>
                    </div>
                  ) : (
                    data.map((property) => (
                      <PropertyCard
                        key={property.id as string}
                        property={property}
                        onView={handleView}
                        onEdit={handleEdit}
                        onCopy={handleCopy}
                        onDelete={handleDeleteClick}
                      />
                    ))
                  )}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={currentPageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* REQUIRED: Filter panel as SIBLING of data-grid-container */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Type"
                options={propertyTypeOptions}
                value={filterType}
                onChange={(val: string | string[] | null) => setFilterType((val as string) || '')}
                placeholder="Select type..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Star Rating"
                options={starRatingOptions}
                value={filterStarRating}
                onChange={(val: string | string[] | null) => setFilterStarRating((val as string) || '')}
                placeholder="Select rating..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>City</label>
              <input
                type="text"
                className="form-input"
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                placeholder="Filter by city..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleFilterApply}>
              Apply
            </button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <PropertyViewModal
        isOpen={viewModalOpen}
        property={selectedRecord}
        onClose={() => setViewModalOpen(false)}
        onEdit={(property) => handleEdit(property)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Form Modal */}
      <PropertyModal
        isOpen={formModalOpen}
        mode={formMode}
        record={formMode === 'edit' ? selectedRecord : selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.propertyName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Property Search Popup (F2) */}
      <SyutSearchGrid<Property>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(property) => handleView(property)}
        title="Search Properties"
        columns={[
          { key: 'propertyCode', header: 'Code', width: '120px', sortable: true },
          { key: 'propertyName', header: 'Property Name', width: '200px', sortable: true },
          { key: 'propertyType', header: 'Type', width: '150px', sortable: true },
          { key: 'city', header: 'City', width: '120px', sortable: true },
          {
            key: 'isActive',
            header: 'Status',
            width: '100px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Active' : 'Inactive'}
              </span>
            ),
          },
        ]}
        data={data}
        filters={[
          {
            key: 'propertyType',
            label: 'Type',
            type: 'select',
            options: [
              { value: 'Hotel', label: 'Hotel' },
              { value: 'Resort', label: 'Resort' },
              { value: 'Serviced Apartment', label: 'Serviced Apartment' },
            ],
          },
          {
            key: 'isActive',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ],
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="property-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default PropertyMasterScreen;
